
import React, { useState } from "react";
import {
  Box,
  Button,
  Modal,
  Typography,
  TextField,
  FormControl,
  InputLabel,
  DialogTitle,
  Select,
  MenuItem,
  FormHelperText,
  CircularProgress,
} from "@mui/material";
import { CloudUpload } from "@mui/icons-material";

const FileUploadPopup = ({
  open,
  onClose,
  onUpload,
  currentFolderId,
  policyId,
  fileType,
  customerId
}) => {
  // State for form fields
  const [file, setFile] = useState(null);
  const [type, setFileType] = useState("");
  const [description, setDescription] = useState("");
  const [isUploading, setIsUploading] = useState(false);

  // Validation state
  const [errors, setErrors] = useState({
    file: false,
    type: false,
  });

  // Handle file selection
  const handleFileChange = (event) => {
    if (event.target.files && event.target.files[0]) {
      setFile(event.target.files[0]);
      // Clear error when file is selected
      setErrors(prev => ({ ...prev, file: false }));
    }
  };

  // Handle form submission
  const handleSubmit = async () => {
    // Validate form
    const newErrors = {
      file: !file,
      type: !type.trim(),
    };

    setErrors(newErrors);

    // If there are errors, don't proceed
    if (newErrors.file || newErrors.type) {
      return;
    }

    try {
      setIsUploading(true);

      // Create FormData object
      const formData = new FormData();
      formData.append("files", file);
      formData.append("parentFolderId", currentFolderId);
      // formData.append("policyId", policyId);
      formData.append("type", type);
      formData.append("description", description);

      // Call the upload function passed from parent
      if (fileType === "policy" && policyId) {
        formData.append("policyId", policyId);
      } else if (fileType === "customer" && customerId) {
        const clientCustomerId=customerId;
        formData.append("clientCustomerId", clientCustomerId);
      } else {

        return;
      }
      await onUpload(formData);

      // Reset form and close modal on success
      resetForm();
      onClose();
    } catch (error) {

    } finally {
      setIsUploading(false);
    }
  };

  // Reset form fields
  const resetForm = () => {
    setFile(null);
    setFileType("");
    setDescription("");
    setErrors({
      file: false,
      type: false,
    });
  };

  // Handle modal close
  const handleClose = () => {
    resetForm();
    onClose();
  };

  return (
    <Modal
      open={open}
      onClose={handleClose}
      aria-labelledby="file-upload-modal-title"
    >
      <Box
        sx={{
          position: "absolute",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          width: 500,
          bgcolor: "background.paper",
          boxShadow: 24,
          borderRadius: 1,
        }}
      >
        <DialogTitle id="file-upload-modal-title">
          Upload File
        </DialogTitle>

        {/* File Selection */}
        <Box sx={{ mb: 3, px: 3, pt: 2 }}>
          <input
            accept="*/*"
            id="contained-button-file"
            type="file"
            style={{ display: 'none' }}
            onChange={handleFileChange}
          />
          <label htmlFor="contained-button-file">
            <Button
              variant="outlined"
              component="span"
              startIcon={<CloudUpload />}
              fullWidth
              sx={{
                p: 1.5,

                borderColor: errors.file ? 'error.main' : 'inherit',
                borderStyle: 'dashed',
                justifyContent: 'flex-start',
              }}
            >
              {file ? file.name : "Select a file to upload"}
            </Button>
          </label>
          {errors.file && (
            <FormHelperText error>Please select a file</FormHelperText>
          )}
        </Box>

        {/* File Type Selection */}
        <FormControl
          fullWidth
          sx={{ mb: 3, px: 3 }}
          error={errors.type}
        >
           <Typography variant="body1">
            File Type
          </Typography>

          <Select
            labelId="file-type-label"
            value={type}
            onChange={(e) => setFileType(e.target.value)}
            disabled={isUploading}
          >
            <MenuItem value="Signed Application">
                 Signed Application
               </MenuItem>
               <MenuItem value="Unsigned Application">
                 Unsigned Application
               </MenuItem>
               <MenuItem value="ID Cards">ID Cards</MenuItem>
               <MenuItem value="Receipt">Receipt</MenuItem>
               <MenuItem value="Pictures">Pictures</MenuItem>
               <MenuItem value="Proof of Prior">Proof of Prior</MenuItem>
               <MenuItem value="Proof of homeownership">
                 Proof of homeownership
               </MenuItem>
               <MenuItem value="Proof of residency">
                 Proof of residency
               </MenuItem>
               <MenuItem value="Driver license">Driver license</MenuItem>
               <MenuItem value="Health Insurance Consent Form">
                 Health Insurance Consent Form
               </MenuItem>
               <MenuItem value="Vehicle Registration">
                 Vehicle Registration
               </MenuItem>
               <MenuItem value="UW Notice">UW Notice</MenuItem>
               <MenuItem value="Cancellation">Cancellation</MenuItem>
               <MenuItem value="Endorsement">Endorsement</MenuItem>
               <MenuItem value="Quote">Quote</MenuItem>
               <MenuItem value="Renewal Package">Renewal Package</MenuItem>
               <MenuItem value="Declaration Page">Declaration Page</MenuItem>
                 <MenuItem value="Other">Other</MenuItem>
          </Select>
          {errors.type && (
            <FormHelperText>Please select a file type</FormHelperText>
          )}
        </FormControl>

        {/* Description Field */}
        <Box sx={{ px: 3 }}>
        <Typography
            variant="body1"            
          >
            Description (Optional)
        </Typography>
        <TextField
          fullWidth
          multiline
          rows={3}
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          sx={{ mb: 3 }}
          disabled={isUploading}
        />
        </Box>
        {/* Action Buttons */}
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb:3, gap: 2, px: 3  }}>
          <Button
            onClick={handleClose}
            variant="outlined"
            disabled={isUploading}
          >
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            variant="contained"
            disabled={isUploading}
          >
            {isUploading ? (
              <>
                <CircularProgress size={24} sx={{ mr: 1, color: 'black' }} />
                Uploading...
              </>
            ) : (
              "Upload"
            )}
          </Button>
        </Box>
      </Box>
    </Modal>
  );
};

export default FileUploadPopup;